import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Quote, Sparkles, Loader2, Save, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const BANNER_LEVELS = [
  { value: "info",     label: "Info (caramel clair)" },
  { value: "success",  label: "Succès (vert/sable)" },
  { value: "warning",  label: "Attention (terracotta)" },
  { value: "premium",  label: "Premium (or)" },
];

export default function AdminDashboardControl() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dashboard-config", { params: { raw: true } }).then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) {
    return (
      <div className="py-24 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" />
      </div>
    );
  }

  const set = (key, patch) => setCfg((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/dashboard-config", {
        banner: cfg.banner,
        quote_of_the_day: cfg.quote_of_the_day,
        announcement: cfg.announcement,
        widgets: cfg.widgets,
      });
      setCfg(data);
      toast.success("Configuration mise à jour");
    } catch (e) {
      toast.error(e?.userMessage || "Échec de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-dashboard-control-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="caption-label text-[#A26F3E]">Pilotage</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Dashboards utilisateurs</h1>
            <p className="text-[#666] mt-2">Contrôlez ce que voient les talents et les créateurs sans toucher au code.</p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white" data-testid="dc-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Banner */}
          <Section icon={Megaphone} title="Bannière annonce" desc="Affichée en haut des dashboards Talent et Créateur.">
            <ToggleRow
              label="Activer la bannière"
              checked={cfg.banner.active}
              onChange={(v) => set("banner", { active: v })}
              testid="dc-banner-toggle"
            />
            <Field label="Titre">
              <Input value={cfg.banner.title} onChange={(e) => set("banner", { title: e.target.value })} className="rounded-xl" data-testid="dc-banner-title" />
            </Field>
            <Field label="Message">
              <Textarea rows={3} value={cfg.banner.message} onChange={(e) => set("banner", { message: e.target.value })} className="rounded-xl" data-testid="dc-banner-message" />
            </Field>
            <Field label="Lien (optionnel)">
              <Input value={cfg.banner.link} onChange={(e) => set("banner", { link: e.target.value })} placeholder="/concours/abc-123 ou https://…" className="rounded-xl" data-testid="dc-banner-link" />
            </Field>
            <Field label="Style">
              <Select value={cfg.banner.level} onValueChange={(v) => set("banner", { level: v })}>
                <SelectTrigger className="rounded-xl" data-testid="dc-banner-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BANNER_LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            <EndsAtField value={cfg.banner.ends_at} expired={cfg.banner.expired} onChange={(v) => set("banner", { ends_at: v })} testid="dc-banner-ends" />

            {/* Live preview */}
            <div className="mt-4">
              <p className="caption-label text-[#8B8680] mb-2">Aperçu</p>
              <DashboardBannerPreview banner={cfg.banner} />
            </div>
          </Section>

          {/* Quote */}
          <Section icon={Quote} title="Citation du jour" desc="Petite touche d'inspiration affichée sur les dashboards.">
            <ToggleRow
              label="Afficher la citation"
              checked={cfg.quote_of_the_day.active}
              onChange={(v) => set("quote_of_the_day", { active: v })}
              testid="dc-quote-toggle"
            />
            <Field label="Citation">
              <Textarea rows={3} value={cfg.quote_of_the_day.text} onChange={(e) => set("quote_of_the_day", { text: e.target.value })} className="rounded-xl" data-testid="dc-quote-text" />
            </Field>
            <Field label="Auteur">
              <Input value={cfg.quote_of_the_day.author} onChange={(e) => set("quote_of_the_day", { author: e.target.value })} className="rounded-xl" data-testid="dc-quote-author" />
            </Field>
            <EndsAtField value={cfg.quote_of_the_day.ends_at} expired={cfg.quote_of_the_day.expired} onChange={(v) => set("quote_of_the_day", { ends_at: v })} testid="dc-quote-ends" />
          </Section>

          {/* Widgets */}
          <Section icon={Sparkles} title="Widgets activables" desc="Active ou désactive des sections complètes des dashboards.">
            <ToggleRow label="Concours du moment"   checked={cfg.widgets.concours_du_moment}  onChange={(v) => set("widgets", { concours_du_moment: v })} testid="dc-widget-concours" />
            <ToggleRow label="Talent de la semaine" checked={cfg.widgets.talent_de_la_semaine} onChange={(v) => set("widgets", { talent_de_la_semaine: v })} testid="dc-widget-talent" />
            <ToggleRow label="Événement à venir"    checked={cfg.widgets.evenement_a_venir}    onChange={(v) => set("widgets", { evenement_a_venir: v })} testid="dc-widget-event" />
            <ToggleRow label="Citation du jour"     checked={cfg.widgets.citation_du_jour}     onChange={(v) => set("widgets", { citation_du_jour: v })} testid="dc-widget-quote" />
          </Section>

          {/* Announcement */}
          <Section icon={Megaphone} title="Annonce plateforme" desc="Petite annonce inline (maintenance, fonctionnalité, etc.).">
            <ToggleRow
              label="Activer l'annonce"
              checked={cfg.announcement.active}
              onChange={(v) => set("announcement", { active: v })}
              testid="dc-announce-toggle"
            />
            <Field label="Message">
              <Textarea rows={2} value={cfg.announcement.message} onChange={(e) => set("announcement", { message: e.target.value })} className="rounded-xl" data-testid="dc-announce-message" />
            </Field>
            <Field label="Lien (optionnel)">
              <Input value={cfg.announcement.link} onChange={(e) => set("announcement", { link: e.target.value })} className="rounded-xl" data-testid="dc-announce-link" />
            </Field>
            <EndsAtField value={cfg.announcement.ends_at} expired={cfg.announcement.expired} onChange={(v) => set("announcement", { ends_at: v })} testid="dc-announce-ends" />
          </Section>
        </div>
      </div>
  );
}

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="card-lux p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FAF3EA] flex items-center justify-center"><Icon className="w-5 h-5 text-[#C68E59]" /></div>
        <div>
          <h3 className="font-serif text-xl text-[#1A1A1A]">{title}</h3>
          <p className="text-xs text-[#666] mt-0.5">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Optional end date — the item disappears from every dashboard automatically once past. */
function EndsAtField({ value, expired, onChange, testid }) {
  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const quick = (days) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(23, 59, 0, 0); onChange(d.toISOString()); };
  const endOfMonth = () => { const d = new Date(); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 0, 0); onChange(e.toISOString()); };
  return (
    <div className="mt-3" data-testid={testid}>
      <Label className="text-xs text-[#6E4B2A] inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Fin d'affichage (optionnel)</Label>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Input type="datetime-local" value={toLocal(value)} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)} className="rounded-xl w-auto" data-testid={`${testid}-input`} />
        <button type="button" onClick={() => quick(1)} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FAF3EA] text-[#6E4B2A] hover:bg-[#EADDC9]" data-testid={`${testid}-24h`}>24 h</button>
        <button type="button" onClick={() => quick(7)} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FAF3EA] text-[#6E4B2A] hover:bg-[#EADDC9]" data-testid={`${testid}-7d`}>7 jours</button>
        <button type="button" onClick={endOfMonth} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FAF3EA] text-[#6E4B2A] hover:bg-[#EADDC9]" data-testid={`${testid}-month`}>Fin du mois</button>
        {value && <button type="button" onClick={() => onChange(null)} className="text-[11px] text-[#A3483F] underline underline-offset-2" data-testid={`${testid}-clear`}>Sans fin</button>}
      </div>
      {value && (
        <p className={`text-[11px] mt-1.5 ${expired ? "text-[#A3483F] font-semibold" : "text-[#8B8680]"}`} data-testid={`${testid}-status`}>
          {expired ? "Expirée — masquée automatiquement sur les dashboards" : `Disparaît automatiquement le ${new Date(value).toLocaleString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}`}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="caption-label">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#FAF3EA] last:border-b-0">
      <span className="text-sm text-[#1A1A1A]">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export function DashboardBannerPreview({ banner }) {
  if (!banner?.active || !banner.message) {
    return <div className="rounded-2xl border border-dashed border-[#EADDC9] p-4 text-center text-xs text-[#8B8680]">Bannière désactivée</div>;
  }
  const styles = {
    info:     { bg: "#FAF3EA", text: "#1A1A1A", accent: "#C68E59" },
    success:  { bg: "#E9EFD7", text: "#3A4A1F", accent: "#7A8E3F" },
    warning:  { bg: "#F3DAD0", text: "#4A1F12", accent: "#B44F3F" },
    premium:  { bg: "#1A1A1A", text: "#F4E4C9", accent: "#E6C29A" },
  }[banner.level || "info"];
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: styles.bg, color: styles.text }}>
      {banner.title && <p className="text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: styles.accent }}>{banner.title}</p>}
      <p className="font-serif text-base mt-1">{banner.message}</p>
      {banner.link && <p className="text-xs mt-2 underline" style={{ color: styles.accent }}>→ Lien actif</p>}
    </div>
  );
}
